/**
 * Provenance du visiteur (cf. utils/provenance.ts) : mémorisée pour la durée
 * de la visite, envoyée à l'API dès qu'un paiement a un identifiant.
 *
 * `suivreProvenancePaiement(ref)` : à appeler dans le setup d'une page de don
 * avec la ref de l'identifiant Stripe qu'elle connaît déjà — LECTURE seule,
 * le code de paiement n'est pas modifié. L'envoi est isolé : il ne lève
 * jamais et n'est jamais attendu par le paiement.
 *
 * ⚠️ État et config sont lus AU SETUP (`creerEnvoiProvenance`), jamais dans
 * un rappel : un composable Nuxt appelé hors contexte casse la page
 * (cf. panne PayPal du 13/10, useI18n hors setup).
 */
import { hasMarketingConsent, readConsent } from "@donoor/utils/consent";
import type { Provenance } from "~/utils/provenance";

const dejaEnvoyees = new Set<string>();

export function useProvenance() {
  return useState<Provenance | null>("donoor-provenance", () => null);
}

/** Prépare l'envoi (à appeler dans un setup ou un plugin). */
export function creerEnvoiProvenance(): (references: Array<string | null | undefined>) => void {
  const provenance = useProvenance();
  const apiBase =
    (useRuntimeConfig().public.apiBaseUrl as string) || "https://api.donoor.org";

  return (references) => {
    try {
      if (import.meta.server) return;
      const p = provenance.value;
      if (!p || (!Object.keys(p.parametres).length && !p.pageArrivee)) return;
      const nouvelles = references.filter(
        (r): r is string => typeof r === "string" && r.length >= 10 && !dejaEnvoyees.has(r),
      );
      if (!nouvelles.length) return;
      nouvelles.forEach((r) => dejaEnvoyees.add(r));

      void fetch(`${apiBase}/api/public/provenance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          references: nouvelles,
          parametres: p.parametres,
          pageArrivee: p.pageArrivee,
          consentementPub: hasMarketingConsent(readConsent()),
        }),
      }).catch(() => {});
    } catch {
      /* jamais bloquant */
    }
  };
}

/** Envoie la provenance dès que l'identifiant de paiement de la page existe. */
export function suivreProvenancePaiement(reference: Ref<string | null | undefined>): void {
  const envoyer = creerEnvoiProvenance();
  watch(reference, (id) => envoyer([id]), { immediate: true });
}
